import { useEffect } from 'react';

import { useRouter, useRouteError } from 'src/routes/hooks';

import { logErrorToDatadog } from '../../observability/datadog-helpers';

const RETRY_KEY = 'error_boundary_retry_count';

export function ErrorBoundaryProd() {
  const error = useRouteError();
  const router = useRouter();

  useEffect(() => {
    // 🚨 1. Log error
    logErrorToDatadog(error);

    // 🔁 2. Read retry count from sessionStorage
    const retryCount = Number(sessionStorage.getItem(RETRY_KEY) || '0');

    const timer = setTimeout(() => {
      if (retryCount < 1) {
        // ✅ First retry → reload
        sessionStorage.setItem(RETRY_KEY, String(retryCount + 1));
        window.location.reload();
      } else {
        // ✅ Second failure → reset + redirect
        sessionStorage.removeItem(RETRY_KEY);
        router.replace('/');
      }
    }, 7000);

    return () => clearTimeout(timer);
  }, [error, router]);

  return (
    <div style={styles.root}>
      <div style={styles.card}>
        <div style={styles.iconWrap} aria-hidden>
          <span style={styles.icon}>⚠️</span>
        </div>

        <h1 style={styles.title}>Something went wrong</h1>

        <p style={styles.description}>
          We&apos;re sorry — something unexpected happened. Our team has been notified and is working
          on it.
        </p>

        <div style={styles.actions}>
          <button style={styles.primaryButton} onClick={() => window.location.reload()}>
            Try Again
          </button>

          <button style={styles.secondaryButton} onClick={() => router.push('/')}>
            Go to Home
          </button>
        </div>

        <div style={styles.footer}>Attempting automatic recovery...</div>
      </div>
    </div>
  );
}

/** Hard-coded to match src/theme (palette + typography); MUI theme is unavailable here. */
const FONT =
  '"Host Grotesk Variable", "Host Grotesk", system-ui, -apple-system, "Segoe UI", sans-serif';

const styles = {
  root: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box' as const,
    background: '#F4F6F8',
    fontFamily: FONT,
    padding: '24px',
    WebkitFontSmoothing: 'antialiased' as const,
    MozOsxFontSmoothing: 'grayscale' as const,
  },

  card: {
    width: '100%',
    maxWidth: '440px',
    background: '#FFFFFF',
    borderRadius: '8px',
    padding: '40px 32px',
    boxShadow: '0 12px 24px -4px rgba(28, 37, 46, 0.12), 0 0 2px 0 rgba(28, 37, 46, 0.08)',
    textAlign: 'center' as const,
    border: '1px solid #DFE3E8',
  },

  iconWrap: {
    width: '56px',
    height: '56px',
    margin: '0 auto 20px',
    borderRadius: '8px',
    background: '#ECDEFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  icon: {
    fontSize: '28px',
    lineHeight: 1,
  },

  title: {
    margin: '0 0 12px',
    fontSize: '1.25rem',
    fontWeight: 700,
    lineHeight: 1.35,
    letterSpacing: 0,
    color: '#1C252E',
  },

  description: {
    margin: '0 0 28px',
    fontSize: '0.875rem',
    fontWeight: 400,
    lineHeight: 1.57,
    letterSpacing: 0,
    color: '#637381',
  },

  actions: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '12px',
    justifyContent: 'center',
  },

  primaryButton: {
    padding: '8px 16px',
    minHeight: '36px',
    borderRadius: '8px',
    border: 'none',
    background: '#9747FF',
    color: '#FFFFFF',
    fontFamily: FONT,
    fontSize: '0.875rem',
    fontWeight: 500,
    lineHeight: 1.71,
    letterSpacing: 0,
    cursor: 'pointer',
  },

  secondaryButton: {
    padding: '8px 16px',
    minHeight: '36px',
    borderRadius: '8px',
    border: '1px solid #DFE3E8',
    background: '#FFFFFF',
    color: '#1C252E',
    fontFamily: FONT,
    fontSize: '0.875rem',
    fontWeight: 500,
    lineHeight: 1.71,
    letterSpacing: 0,
    cursor: 'pointer',
  },

  footer: {
    marginTop: '24px',
    fontSize: '0.75rem',
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: 0,
    color: '#919EAB',
  },
};
